import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FiClock, FiCalendar, FiUser, FiEye, FiLink, FiCheck, FiArrowRight, FiList } from "react-icons/fi";
import { FaFacebookF, FaLinkedinIn, FaWhatsapp, FaXTwitter } from "react-icons/fa6";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BlogCard, { BlogThumb, CategoryPill, formatDate } from "../components/blog/BlogCard";
import "../components/blog/blog.css";
import useSeo from "../hooks/useSeo";
import { apiRequest } from "../api";

function ShareBar({ url, title }) {
  const [copied, setCopied] = useState(false);
  const link = encodeURIComponent(url);
  const text = encodeURIComponent(title);
  const targets = [
    { label: "X", Icon: FaXTwitter, href: `https://twitter.com/intent/tweet?url=${link}&text=${text}` },
    { label: "LinkedIn", Icon: FaLinkedinIn, href: `https://www.linkedin.com/sharing/share-offsite/?url=${link}` },
    { label: "Facebook", Icon: FaFacebookF, href: `https://www.facebook.com/sharer/sharer.php?u=${link}` },
    { label: "WhatsApp", Icon: FaWhatsapp, href: `https://wa.me/?text=${text}%20${link}` },
  ];
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { window.prompt("Copy this link", url); }
  };
  const button = "w-10 h-10 rounded-full border border-navy-border bg-white/5 flex items-center justify-center text-gray-300 hover:text-white hover:border-accent-light hover:bg-accent/20 transition-colors";
  return (
    <div className="flex items-center gap-2" aria-label="Share this article">
      {targets.map(({ label, Icon, href }) => (
        <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={`Share on ${label}`} title={`Share on ${label}`} className={button}><Icon size={15} /></a>
      ))}
      <button type="button" onClick={copy} aria-label="Copy link" title="Copy link" className={button}>{copied ? <FiCheck size={16} className="text-emerald-400" /> : <FiLink size={16} />}</button>
    </div>
  );
}

function TableOfContents({ items, active }) {
  if (!items.length) return null;
  return (
    <nav aria-label="Table of contents" className="rounded-2xl border border-navy-border bg-white/5 p-5">
      <p className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-accent-light mb-3"><FiList size={14} /> In this article</p>
      <ol className="space-y-1 max-h-[55vh] overflow-y-auto pr-1">
        {items.filter((i) => i.level === 2).map((item) => (
          <li key={item.id}>
            <a href={`#${item.id}`} className={`block text-sm leading-snug py-1.5 pl-3 border-l-2 transition-colors ${active === item.id ? "border-accent-light text-white font-semibold" : "border-white/10 text-gray-400 hover:text-white hover:border-white/40"}`}>{item.text}</a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined);        // undefined = loading, null = not found
  const [progress, setProgress] = useState(0);
  const [active, setActive] = useState("");
  const navigate = useNavigate();

  // Links inside the article (written in the admin editor) are plain <a> tags: the ones to this website move within the app instead of reloading it
  const openInternalLink = (event) => {
    const link = event.target.closest && event.target.closest("a");
    const href = link?.getAttribute("href") || "";
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || link.target === "_blank") return;
    if (href.startsWith("#")) return;
    if (href.startsWith("/") && !href.startsWith("//")) { event.preventDefault(); navigate(href); }
  };

  useEffect(() => {
    let alive = true;
    setPost(undefined);
    window.scrollTo(0, 0);
    apiRequest(`/core/blog-posts/slug/${encodeURIComponent(slug)}/`).then(({ ok, data }) => alive && setPost(ok ? data : null));
    return () => { alive = false; };
  }, [slug]);

  const seo = post?.seo;
  useSeo({
    title: post ? seo.title : "Article not found | Mr. Aali Online Technologies",
    description: post ? seo.description : "This article could not be found.",
    canonical: post ? seo.url : undefined,
    image: seo?.image,
    type: "article",
    robots: post === null ? "noindex" : undefined,
    keywords: post?.keywords?.join(", "),
    published: post?.published_at,
    modified: post?.updated_at,
    section: post?.category_label,
    structuredData: seo?.structured_data,
  });

  // The reading-progress bar and the "you are here" mark in the table of contents
  useEffect(() => {
    if (!post) return undefined;
    const headings = () => Array.from(document.querySelectorAll(".blog-prose h2[id]"));
    const onScroll = () => {
      const article = document.getElementById("blog-article");
      if (article) {
        const box = article.getBoundingClientRect();
        const total = box.height - window.innerHeight * 0.6;
        setProgress(total > 0 ? Math.min(100, Math.max(0, (-box.top / total) * 100)) : 0);
      }
      let current = "";
      headings().forEach((h) => { if (h.getBoundingClientRect().top < 140) current = h.id; });
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [post]);

  const url = seo?.url || (typeof window !== "undefined" ? window.location.href : "");

  return (
    <>
      <Header />
      <div className="h-16" />
      <div className="fixed top-16 left-0 right-0 h-1 z-40 bg-transparent" aria-hidden="true">
        <div className="h-full bg-brand-gradient transition-[width] duration-100" style={{ width: `${progress}%` }} />
      </div>

      <main className="bg-navy text-white min-h-[70vh]">
        {post === undefined && <p className="text-gray-400 text-center py-32">Loading article...</p>}
        {post === null && (
          <div className="text-center py-32 px-6">
            <h1 className="text-3xl font-bold">Article not found</h1>
            <p className="text-gray-400 mt-3">It may have been moved or is no longer published.</p>
            <Link to="/blogs" className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-brand-gradient font-semibold">Browse all articles <FiArrowRight /></Link>
          </div>
        )}

        {post && (
          <>
            <header className="relative overflow-hidden border-b border-navy-border">
              <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 15% 0%, rgba(34,211,238,.22), transparent 45%), radial-gradient(circle at 90% 30%, rgba(11,94,215,.3), transparent 50%)" }} aria-hidden="true" />
              {/* The thumbnail sits inside the hero, beside the headline; the article starts below it */}
              <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14 grid lg:grid-cols-2 gap-8 lg:gap-14 items-center">
                <div>
                  <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-sm text-gray-400 mb-5">
                    <Link to="/" className="hover:text-white">Home</Link><span aria-hidden="true">/</span>
                    <Link to="/blogs" className="hover:text-white">Blogs</Link><span aria-hidden="true">/</span>
                    <Link to={`/${post.category_page}`} className="hover:text-white">{post.category_label}</Link>
                  </nav>
                  <CategoryPill category={post.category} />
                  <h1 className="text-3xl md:text-4xl xl:text-5xl font-extrabold leading-tight tracking-tight mt-4">{post.title}</h1>
                  <p className="text-base md:text-lg text-gray-300 leading-relaxed mt-4">{post.excerpt}</p>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-400 mt-6">
                    <span className="flex items-center gap-1.5"><FiUser size={14} /> {post.author_name}</span>
                    <span className="flex items-center gap-1.5"><FiCalendar size={14} /> <time dateTime={post.published_at}>{formatDate(post.published_at)}</time></span>
                    <span className="flex items-center gap-1.5"><FiClock size={14} /> {post.reading_minutes} min read</span>
                    <span className="flex items-center gap-1.5"><FiEye size={14} /> {post.views.toLocaleString()} views</span>
                  </div>
                </div>
                <div className="rounded-3xl overflow-hidden aspect-[16/10] border border-navy-border shadow-2xl shadow-black/40 bg-navy-dark">
                  <BlogThumb post={post} eager />
                </div>
              </div>
            </header>

            <div className="max-w-6xl mx-auto px-6 py-12 grid lg:grid-cols-[minmax(0,1fr)_280px] gap-12 items-start">
              <article id="blog-article" className="min-w-0 max-w-3xl w-full mx-auto">
                {post.toc.some((i) => i.level === 2) && (
                  <details className="lg:hidden mb-8 rounded-2xl border border-navy-border bg-white/5 p-4">
                    <summary className="flex items-center gap-2 cursor-pointer text-sm font-bold text-accent-light"><FiList size={14} /> In this article</summary>
                    <ol className="mt-3 space-y-1">
                      {post.toc.filter((i) => i.level === 2).map((item) => <li key={item.id}><a href={`#${item.id}`} className="block py-1.5 pl-3 border-l-2 border-white/10 text-sm text-gray-300">{item.text}</a></li>)}
                    </ol>
                  </details>
                )}
                <div className="blog-prose" onClick={openInternalLink} dangerouslySetInnerHTML={{ __html: post.content }} />

                {post.keywords.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-10" aria-label="Topics">
                    {post.keywords.slice(0, 10).map((k) => <span key={k} className="px-3 py-1 rounded-full bg-white/5 border border-navy-border text-xs text-gray-300">#{k.replace(/\s+/g, "")}</span>)}
                  </div>
                )}

                <div className="mt-10 pt-6 border-t border-navy-border flex flex-wrap items-center justify-between gap-4">
                  <p className="font-semibold">Found this useful? Share it.</p>
                  <ShareBar url={url} title={post.title} />
                </div>

                <aside className="mt-12 rounded-3xl p-8 md:p-10 text-center border border-accent-light/30" style={{ background: "linear-gradient(135deg, rgba(34,211,238,.12), rgba(11,94,215,.22))" }}>
                  <h2 className="text-2xl md:text-3xl font-extrabold">Ready to turn ideas into results?</h2>
                  <p className="text-gray-300 mt-3 max-w-xl mx-auto">Talk to the Mr. Aali Online Technologies team about software, AI tools and digital growth for your business.</p>
                  <div className="flex flex-wrap justify-center gap-3 mt-6">
                    <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-gradient font-semibold shadow-lg shadow-accent/20">Contact us <FiArrowRight /></Link>
                    <Link to="/portfolio" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-navy-border hover:border-accent-light font-semibold">See our work</Link>
                  </div>
                </aside>
              </article>

              <aside className="hidden lg:block lg:sticky lg:top-28 space-y-5">
                <TableOfContents items={post.toc} active={active} />
                <div className="rounded-2xl border border-navy-border bg-white/5 p-5">
                  <p className="text-xs uppercase tracking-widest font-bold text-accent-light mb-3">Share</p>
                  <ShareBar url={url} title={post.title} />
                </div>
              </aside>
            </div>

            {post.related.length > 0 && (
              <section className="border-t border-navy-border bg-navy-dark/40 py-14" aria-label="Related articles">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                  <h2 className="text-2xl md:text-3xl font-extrabold mb-8">Keep reading</h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">{post.related.map((p) => <BlogCard key={p.id} post={p} />)}</div>
                </div>
              </section>
            )}
          </>
        )}
      </main>
      <Footer />
    </>
  );
}
